import { inlineSelectStyles } from '@upright/ui/common/generic/utilities/selectStyles';
import { useMemo, useCallback } from 'react';
import Select, { StylesConfig } from 'react-select';

interface SelectorCellProps {
  value: string;
  onUpdate: (newValue: string) => void;
  selectedValues?: string[]; // List of already-selected values
  currentValue?: string; // Current row's value (for self-exclusion)
  availableValues: readonly string[];
  placeHolder?: string;
  /**
   * Minimum width for the open dropdown menu (e.g. '260px'). The menu otherwise matches the
   * (often narrow) cell width, wrapping/clipping long options; set this when the option labels
   * are longer than the column — the menu widens while the in-cell control stays column-width.
   */
  menuMinWidth?: string;
}

/**
 * Editable cell with searchable value dropdown picker
 * Uses the provided list of available values for options
 * Filters out already-selected countries to prevent duplicates
 */
export function SelectorCell({
  value,
  onUpdate,
  selectedValues = [],
  currentValue,
  availableValues,
  placeHolder,
  menuMinWidth,
}: SelectorCellProps) {
  // Filter out selected countries, but keep current value available
  const options = useMemo(() => {
    return availableValues.filter(option => !selectedValues.includes(option) || option === currentValue);
  }, [selectedValues, currentValue, availableValues]);

  // Widen the open menu past the cell width when requested, so long option labels don't wrap.
  const styles = useMemo<StylesConfig<{ label: string; value: string }, false>>(
    () =>
      menuMinWidth
        ? {
            ...valuePickerStyles,
            menu: (base, props) => ({ ...valuePickerStyles.menu?.(base, props), minWidth: menuMinWidth }),
          }
        : valuePickerStyles,
    [menuMinWidth],
  );

  const selectValue = useMemo(() => (value ? { label: value, value: value } : null), [value]);

  const selectOptions = useMemo(() => options.map(name => ({ label: name, value: name })), [options]);

  const handleChange = useCallback(
    (option: { label: string; value: string } | null) => {
      if (option) {
        onUpdate(option.value);
      }
    },
    [onUpdate],
  );

  return (
    <Select<{ label: string; value: string }, false>
      value={selectValue}
      onChange={handleChange}
      options={selectOptions}
      components={{
        DropdownIndicator: null,
        IndicatorSeparator: null,
      }}
      styles={styles}
      placeholder={placeHolder}
      isClearable={false}
      isSearchable={true}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      filterOption={(option, inputValue) => {
        return option.label.toLowerCase().includes(inputValue.toLowerCase());
      }}
    />
  );
}

/**
 * Reusable styles for value picker dropdown in table cells
 */
const valuePickerStyles: StylesConfig<{ label: string; value: string }, false> = {
  ...inlineSelectStyles,
  container: base => ({
    ...base,
    width: '100%',
  }),
  control: base => ({
    ...base,
    minHeight: 'auto',
    border: '1px solid var(--borderColor-default)',
    background: 'var(--bgColor-default)',
    fontSize: 'var(--font-size__md)',
  }),
  valueContainer: base => ({
    ...base,
    padding: '2px 8px',
  }),
  input: base => ({
    ...base,
    fontSize: 'var(--font-size__md)',
  }),
  singleValue: base => ({
    ...base,
    fontSize: 'var(--font-size__md)',
  }),
  placeholder: base => ({
    ...base,
    fontSize: 'var(--font-size__md)',
  }),
  menu: base => ({
    ...base,
    zIndex: 9999,
    fontSize: 'var(--font-size__md)',
  }),
  option: base => ({
    ...base,
    fontSize: 'var(--font-size__md)',
  }),
};
